import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Loader2, Plus, Search, ArrowRight } from "lucide-react";
import { toast } from "sonner";

import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { adminListCompanies, adminCreateCompany, adminListUsers } from "@/lib/api";
import {
  ActiveBadge, DemoBadge, TokenAlertBadge, Field, errDetail,
} from "@/components/company/CompanyUi";

const EMPTY = { name: "", slug: "", contact_email: "", contact_phone: "", country: "", notes: "", seats_plan: "" };

export default function AdminCompanies() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, u] = await Promise.all([
        adminListCompanies(),
        adminListUsers({}).catch(() => ({ users: [] })),
      ]);
      setItems(c.companies || []);
      setUsers(Object.fromEntries((u.users || []).map((x) => [x.id, x])));
    } catch (e) {
      toast.error(errDetail(e, "No se pudo cargar la lista de clientes."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((c) =>
      [c.name, c.slug, c.contact_email, c.country, users[c.master_user_id]?.email]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(t)));
  }, [items, q, users]);

  const seatsInvalid = form.seats_plan !== "" && (!Number.isInteger(Number(form.seats_plan)) || Number(form.seats_plan) < 0 || Number(form.seats_plan) % 5 !== 0);

  const create = async () => {
    if (!form.name.trim()) {
      toast.error("Indique el nombre del cliente.");
      return;
    }
    if (seatsInvalid) {
      toast.error("Los seats del plan deben ser múltiplos de 5.");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, name: form.name.trim(), seats_plan: Number(form.seats_plan || 0) };
      if (!body.slug.trim()) delete body.slug;
      const { company } = await adminCreateCompany(body);
      toast.success(`Cliente creado: ${company.name}`);
      setOpen(false);
      setForm(EMPTY);
      nav(`/admin/clientes/${company.slug}`);
    } catch (e) {
      toast.error(errDetail(e, "No se pudo crear el cliente."));
    } finally {
      setSaving(false);
    }
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen bg-white text-ink" data-testid="admin-companies-page">
      <TopNav />
      <div className="mx-auto max-w-screen-xl px-6 py-12 sm:py-16">
        <p className="eyebrow flex items-center gap-1.5">
          <Building2 className="h-3 w-3" /> Administración · Clientes
        </p>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl text-ink sm:text-5xl">Clientes</h1>
            <p className="mt-3 max-w-2xl text-sm text-slate-600">
              Fichas de las empresas cliente: estado, usuario master, agentes, plan de seats y alertas de consumo de
              tokens de sus claves de IA.
            </p>
          </div>
          <Button onClick={() => setOpen(true)}
                  className="rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-slate-800"
                  data-testid="new-company-btn">
            <Plus className="mr-2 h-3.5 w-3.5" /> Nuevo cliente
          </Button>
        </div>

        <div className="mt-8 flex max-w-md items-center gap-2 border border-slate-300 px-3 focus-within:border-slate-900">
          <Search className="h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, slug, correo o país"
                 className="h-10 flex-1 bg-transparent text-sm outline-none" data-testid="companies-search" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando…
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-8 border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500"
               data-testid="companies-empty">
            {items.length ? "Ningún cliente coincide con la búsqueda." : "Todavía no hay clientes."}
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto border border-slate-200">
            <table className="w-full min-w-[960px] text-left text-sm" data-testid="companies-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Demo</th>
                  <th className="px-4 py-3 font-medium">Usuario master</th>
                  <th className="px-4 py-3 font-medium">Agentes activos / total</th>
                  <th className="px-4 py-3 font-medium">Seats</th>
                  <th className="px-4 py-3 font-medium">Tokens</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filtered.map((c) => {
                  const m = c.master_user_id ? users[c.master_user_id] : null;
                  return (
                    <tr key={c.slug} className="hover:bg-slate-50" data-testid={`company-row-${c.slug}`}>
                      <td className="px-4 py-3">
                        <Link to={`/admin/clientes/${c.slug}`} className="font-display text-base text-ink hover:text-accent-blue"
                              data-testid={`company-link-${c.slug}`}>
                          {c.name}
                        </Link>
                        <p className="font-mono text-[11px] text-slate-500">{c.slug}</p>
                      </td>
                      <td className="px-4 py-3"><ActiveBadge active={c.active} testid={`company-active-${c.slug}`} /></td>
                      <td className="px-4 py-3">{c.is_demo ? <DemoBadge /> : <span className="text-slate-400">No</span>}</td>
                      <td className="px-4 py-3 text-[12px]">
                        {m ? <span className="font-mono text-accent-blue">{m.email}</span>
                          : c.master_user_id ? <span className="text-slate-500">Designado</span>
                            : <span className="text-slate-400">Sin designar</span>}
                      </td>
                      <td className="px-4 py-3 font-mono text-[12px]" data-testid={`company-agents-${c.slug}`}>
                        {c.agents_active ?? 0} / {c.agents_total ?? c.agents ?? 0}
                      </td>
                      <td className="px-4 py-3 font-mono text-[12px]">{c.seats_plan || 0}</td>
                      <td className="px-4 py-3">
                        <TokenAlertBadge level={c.token_alert_level || "none"} testid={`company-alert-${c.slug}`} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/admin/clientes/${c.slug}`}
                              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] text-slate-700 hover:text-accent-blue"
                              data-testid={`company-open-${c.slug}`}>
                          Ver ficha <ArrowRight className="h-3 w-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => !saving && setOpen(v)}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="new-company-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Nuevo cliente</DialogTitle>
            <DialogDescription>
              Cree la ficha de la empresa. Después podrá designar el usuario master, el enlace de entrevista y las claves de IA.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nombre" required>
              <Input value={form.name} onChange={(e) => upd("name", e.target.value)} className="rounded-sm" data-testid="nc-name" />
            </Field>
            <Field label="Slug" hint="Opcional. Se genera del nombre; no se puede cambiar después.">
              <Input value={form.slug} onChange={(e) => upd("slug", e.target.value.toLowerCase())} className="rounded-sm font-mono" data-testid="nc-slug" />
            </Field>
            <Field label="Correo de contacto">
              <Input type="email" value={form.contact_email} onChange={(e) => upd("contact_email", e.target.value)} className="rounded-sm" data-testid="nc-email" />
            </Field>
            <Field label="Teléfono">
              <Input value={form.contact_phone} onChange={(e) => upd("contact_phone", e.target.value)} className="rounded-sm" data-testid="nc-phone" />
            </Field>
            <Field label="País">
              <Input value={form.country} onChange={(e) => upd("country", e.target.value)} className="rounded-sm" data-testid="nc-country" />
            </Field>
            <Field label="Seats del plan" hint={seatsInvalid ? "Debe ser un múltiplo de 5 (5, 10, 15…)." : "Múltiplos de 5."}>
              <Input type="number" min={0} step={5} value={form.seats_plan} onChange={(e) => upd("seats_plan", e.target.value)}
                     className={`rounded-sm font-mono ${seatsInvalid ? "border-red-400" : ""}`} data-testid="nc-seats" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Notas">
                <Textarea rows={3} value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="rounded-sm" data-testid="nc-notes" />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving} className="rounded-sm" data-testid="nc-cancel">
              Cancelar
            </Button>
            <Button onClick={create} disabled={saving} className="rounded-sm bg-slate-900 text-white hover:bg-slate-800" data-testid="nc-submit">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Crear cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
